import { Button, Tooltip } from '@mui/material';
import useAppTheme from 'hooks/useAppTheme';
import React, { type CSSProperties } from 'react';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface RiskStatDetail {
  value: number | string | undefined;
  label: string;
}

interface RiskStatBlockProps {
  value: number | string | undefined;
  label: string;
  type: 'high' | 'medium' | 'low';
}

interface DetailItem {
  label: string;
  value: string | number;
}

interface AssetOrComponentCardProps {
  imageUrl?: string;
  imageAlt?: string;
  title?: string;
  subtitle?: string;
  additionalSubtitle?: string;
  risks?: {
    high?: RiskStatDetail;
    medium?: RiskStatDetail;
    low?: RiskStatDetail;
  };
  details?: DetailItem[];
  onViewDetails?: () => void;
  viewDetailsText?: string;
}

const RiskStatBlock: React.FC<RiskStatBlockProps> = ({
  value,
  label,
  type,
}) => {
  const blockStyle: CSSProperties = {
    flexGrow: 1,
    textAlign: 'center',
    padding: '0.5rem',
    borderRadius: '0.5rem',
  };
  const valueStyle: CSSProperties = {
    fontSize: '1.5rem',
    fontWeight: 800,
    lineHeight: 1.2,
    margin: 0,
  };
  const labelStyle: CSSProperties = {
    fontSize: '0.6875rem',
    fontWeight: 500,
    letterSpacing: '0.025em',
    textTransform: 'uppercase',
    margin: 0,
  };

  if (type === 'high') {
    blockStyle.backgroundColor = 'rgba(239, 68, 68, 0.1)';
    valueStyle.color = '#ef4444';
    labelStyle.color = '#f87171';
  } else if (type === 'medium') {
    blockStyle.backgroundColor = 'rgba(234, 179, 8, 0.1)';
    valueStyle.color = '#eab308';
    labelStyle.color = '#facc15';
  } else if (type === 'low') {
    blockStyle.backgroundColor = 'rgba(34, 197, 94, 0.1)';
    valueStyle.color = '#22c55e';
    labelStyle.color = '#4ade80';
  }

  return (
    <div style={blockStyle}>
      <p style={valueStyle}>{value}</p>
      <p style={labelStyle}>{label}</p>
    </div>
  );
};

const AssetOrComponentCard: React.FC<AssetOrComponentCardProps> = ({
  imageUrl,
  imageAlt = 'Item image',
  title,
  subtitle,
  additionalSubtitle,
  risks,
  details,
  onViewDetails,
  viewDetailsText = 'VIEW DETAILS',
}) => {
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const cardBase = {
    backgroundColor: palette.boxBackground,
    color: palette.textPrimary,
    border: `1px solid ${palette.borderGrey ?? '#2F445E'}`,
  };

  const lightOverrides = {
    backgroundColor: palette.boxBackground,
    color: palette.textPrimary,
    border: `1px solid ${palette.borderGrey ?? '#e2e8f0'}`,
  };

  const labelColor = palette.grey;
  const valueColor = palette.textPrimary;

  return (
    <div
      style={{
        borderRadius: '8px',
        boxShadow:
          '0 10px 20px -5px rgba(0, 0, 0, 0.1), 0 4px 8px -4px rgba(0, 0, 0, 0.08)',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        ...(enableDarkTheme ? cardBase : lightOverrides),
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: '0.75rem',
          padding: '1rem',
          backgroundColor: enableDarkTheme
            ? palette.toolBarBackground
            : palette.toolBarBackground,
        }}
      >
        <img
          src={imageUrl ?? ''}
          alt={imageAlt}
          style={{
            width: '3rem',
            height: '3rem',
            objectFit: 'cover',
            borderRadius: '9999px',
            flexShrink: 0,
            border: '2px solid rgba(255, 255, 255, 0.1)',
          }}
        />

        <div
          style={{
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            justifyContent: 'center',
          }}
        >
          {title ? (
            <Tooltip title={title ?? ''} arrow>
              <h3
                style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  color: palette.textPrimary,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  margin: '0px',
                }}
              >
                {title ?? ''}
              </h3>
            </Tooltip>
          ) : null}
          {subtitle ? (
            <Tooltip title={subtitle ?? ''} arrow>
              <p
                style={{
                  fontSize: '0.875rem',
                  color: labelColor,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  margin: 0,
                }}
              >
                {subtitle ?? ''}
              </p>
            </Tooltip>
          ) : null}
          {additionalSubtitle ? (
            <Tooltip title={additionalSubtitle} arrow>
              <p
                style={{
                  fontSize: '0.875rem',
                  color: labelColor,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  margin: 0,
                }}
              >
                {additionalSubtitle ?? ''}
              </p>
            </Tooltip>
          ) : null}
        </div>
      </div>
      <div
        style={{
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          flexGrow: 1,
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: '0.75rem',
            width: '100%',
          }}
        >
          {risks?.high ? (
            <RiskStatBlock
              value={risks?.high?.value}
              label={risks?.high.label ?? 'N/A'}
              type="high"
            />
          ) : null}
          {risks?.medium ? (
            <RiskStatBlock
              value={risks?.medium?.value}
              label={risks?.medium.label ?? 'N/A'}
              type="medium"
            />
          ) : null}
          {risks?.low ? (
            <RiskStatBlock
              value={risks?.low?.value}
              label={risks?.low.label ?? 'N/A'}
              type="low"
            />
          ) : null}
        </div>
        {details
          ? details.map((detail, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '0.875rem',
                  color: labelColor,
                  paddingTop: '0.5rem',
                  borderTop: `1px solid ${palette.cartesianGrid}`,
                  lineHeight: 1.4,
                }}
              >
                <span>{detail.label}</span>
                <span
                  style={{
                    fontWeight: 600,
                    color: valueColor,
                  }}
                >
                  {detail.value}
                </span>
              </div>
            ))
          : null}
      </div>
      <div
        style={{
          padding: '0.75rem 1rem 1rem',
          marginTop: 'auto',
        }}
      >
        <Button
          size="small"
          sx={{
            width: '100%',
            height: '100%',
            border: `1px solid ${palette.primary}`,
            borderRadius: '5px',
            py: '4px',
            textTransform: 'none',
          }}
          color="primary"
          onClick={onViewDetails}
          variant="contained"
        >
          {'View details'}
        </Button>
      </div>
    </div>
  );
};

export default AssetOrComponentCard;
